//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { isDefined } from '@utils/filterPredicates';

export interface CheckboxSetFilterProps<T extends FilterValue> {
  isDisabled?: boolean;
  section: FilterSection<T>;
  subsection?: FilterSection<T>;
  onChange: (section: FilterSection<T>) => void;
  isSingleSelect?: boolean;
}

export const CheckboxSetFilter = <T extends FilterValue>({
  isDisabled,
  isSingleSelect = false,
  onChange,
  section,
  subsection,
}: CheckboxSetFilterProps<T>) => {
  const handleSelect = (selected: FilterOption<T>) => {
    const options = section.options.map((option) => ({
      ...option,
      isSelected:
        option === selected ? !selected.isSelected : option.isSelected,
    }));
    onChange({ ...section, options });
  };

  const handleSingleSelect = (selected: FilterOption<T>) => {
    // treated like a radio button - only one checkbox can be selected at a time
    const options = section.options.map((option) => ({
      ...option,
      isSelected: option === selected ? !selected.isSelected : false,
    }));
    onChange({ ...section, options });

    // clear the subsection filters when the parent section changes
    if (isDefined(subsection)) {
      onChange({
        ...subsection,
        options: [],
      });
    }
  };

  return (
    <FiledSet>
      <Legend>{FilterSectionTitle[section.paramKey]}</Legend>
      <CheckboxSet>
        {section.options.map((option) => (
          <Checkbox
            key={option.label}
            handleClick={() =>
              isSingleSelect ? handleSingleSelect(option) : handleSelect(option)
            }
            isChecked={option.isSelected}
            isDisabled={isDisabled}
            label={option.label}
          />
        ))}
      </CheckboxSet>
    </FiledSet>
  );
};

const FiledSet = styled.fieldset`
  padding: unset;
  border: unset;
`;

const Legend = styled.legend(
  ({ theme }) => `
    visibility: hidden;
    height: ${theme.spacings[0]};
    margin: ${theme.spacings[0]};
  `,
);

const CheckboxSet = styled.div`
  display: flex;
  flex-direction: column;
`;

const Checkbox = styled(LabeledCheckbox)(
  ({ theme }) => `
    height: unset;
    padding: ${theme.spacings[2]} ${theme.spacings[0]};
  `,
);
